import { StrictFetcher } from '@this/src/util';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import _ from 'lodash';
import { createTheme } from '@this/constants/themes';

import Hotel from '@this/domain/hotel/hotel';
import MarginType from '@this/domain/organization/margin_type2';
import type { HotelJson } from '@this/domain/select_repository';
import { AITThemeProvider } from '@this/shared/ui/theme';
import type HotelStayPlan from '@this/domain/hotel/hotel_stay_plan';
import { reportError } from '@this/lib/bugsnag';
import HotelBoxTemplate from './hotel_box.template';
import type { HotelBoxProps } from './types';
import type { HotelResponse } from './hotel_candidate_box';

const HotelBox: React.FC<HotelBoxProps> = ({
  destLocation,
  me,
  hotel,
  hotels,
  searchQueryId,
  user: initialUser,
  isDomestic: initialIsDomestic,
  handleSelect,
  handleClickTab,
  selected,
  disabled,
  tab,
  hotelPriceLimit,
  serviceId,
  isRequestForm,
  inList,
  query
}) => {
  const [user, setUser] = useState(initialUser);
  const [isDomestic, setIsDomestic] = useState(initialIsDomestic);
  const isInitialMount = useRef(true);

  const theme = useMemo(() => createTheme(serviceId), [serviceId]);

  const handleChange = useCallback(() => {
    if (handleClickTab && tab !== undefined) {
      handleClickTab(selected ? 'none' : tab);
    }
  }, [handleClickTab, tab, selected]);

  const handleChangeHotelStayPlan = useCallback(
    (hotelJson: HotelJson, selectedPlan: HotelStayPlan, showFee: boolean, marginType: MarginType | undefined) => {
      const includeTax = hotels.includeTax;
      const updatedHotel = new Hotel(
        _.merge(hotelJson, {
          plan_name: selectedPlan.name,
          selected_stay_plan_id: selectedPlan.id,
          room_type: selectedPlan.room_type ? selectedPlan.room_type : selectedPlan.name,
          price: selectedPlan.total_fee,
          bed_types: selectedPlan.bed_types,
          average_price: selectedPlan.average_price,
          average_price_with_tax: selectedPlan.average_price_with_tax,
          includeTax,
          showFee,
          marginType,
          walkminute: hotel.walkminute,
          loading: false
        })
      );

      hotels.updateHotelList(updatedHotel);
      // グローバルなapp.render()を呼び出して全体を再レンダリング
      // HotelListはMobXを使用していないため、手動での再レンダリングが必要
      app.render();
    },
    [hotels, hotel.walkminute]
  );

  // TODO: R-Connect対応
  // 楽天staticファイル検索を利用時に初期選択されたホテルの最新情報を取得するためのイベントハンドラ
  const handleFetchHotel = useCallback(() => {
    hotel.setHotelLoading(true);
    StrictFetcher.get<HotelResponse>(
      `/hotels/${hotel.id}?search_query_id=${searchQueryId}`,
      'hotels/show.json'
    ).then(
      result => {
        if (result.hotel.sold_out) {
          hotel.setSoldOut(true);
        } else if (result.hotel.too_late) {
          hotel.setTooLate(true);
        } else {
          let marginType: MarginType | undefined;
          if (result.margin_type) {
            marginType = new MarginType(result.margin_type);
          }
          const includeTax = hotels.includeTax;
          const updatedHotel = new Hotel(
            _.merge(result.hotel, {
              includeTax,
              showFee: result.show_fee,
              marginType,
              loading: false
            })
          );

          setUser(result.user);
          setIsDomestic(result.is_domestic);
          hotels.updateHotelList(updatedHotel);
        }
        hotel.setHotelLoading(false);
      },
      () => {
        hotel.setHotelLoading(false);
      }
    );
  }, [hotel, searchQueryId, hotels]);

  useEffect(() => {
    // 楽天staticファイルから取得されたホテルの場合にのみfetch処理を発火
    // 初回マウント時のみ実行（hotel オブジェクトが変わっても再実行は不要）
    if (isInitialMount.current) {
      isInitialMount.current = false;

      if (hotel.operator_class_name === 'RakutenStaticfileOperator') {
        handleFetchHotel();
      }
    }
  }, [hotel.operator_class_name, handleFetchHotel, hotel.id]);

  try {
    return serviceId ? (
      <AITThemeProvider theme={theme}>
        <HotelBoxTemplate
          me={me}
          destLocation={destLocation}
          selected={selected}
          inList={inList}
          hotel={hotel}
          hotels={hotels}
          hotelPriceLimit={hotelPriceLimit}
          isRequestForm={isRequestForm}
          disabled={disabled}
          query={query}
          searchQueryId={searchQueryId}
          handleSelect={handleSelect}
          handleClickTab={handleClickTab}
          tab={tab}
          serviceId={serviceId}
          user={user}
          isDomestic={isDomestic}
          handleChange={handleChange}
          handleChangeHotelStayPlan={handleChangeHotelStayPlan}
        />
      </AITThemeProvider>
    ) : (
      <HotelBoxTemplate
        me={me}
        destLocation={destLocation}
        selected={selected}
        inList={inList}
        hotel={hotel}
        hotels={hotels}
        hotelPriceLimit={hotelPriceLimit}
        isRequestForm={isRequestForm}
        disabled={disabled}
        query={query}
        searchQueryId={searchQueryId}
        handleSelect={handleSelect}
        handleClickTab={handleClickTab}
        tab={tab}
        serviceId={serviceId}
        user={user}
        isDomestic={isDomestic}
        handleChange={handleChange}
        handleChangeHotelStayPlan={handleChangeHotelStayPlan}
      />
    );
  } catch (e) {
    reportError(e);
    return null;
  }
};

export default HotelBox;
